import '@justgiving/carepack-forms/forms.css';
import '@justgiving/carepack-design-tokens/dist/colors.cssmodules.css';
import '@justgiving/carepack-design-tokens/dist/layout.cssmodules.css';
import '@justgiving/carepack-design-tokens/dist/typography.cssmodules.css';
import '@justgiving/loading/src/Loading.scss';
import '@justgiving/carepack-typography';
import '@justgiving/carepack-core';
import '@justgiving/carepack-dialog/dialog.css';
import '@justgiving/carepack-toast/toast.css';
import '@justgiving/carepack-chips/chips.css';
import '@justgiving/carepack-snackbar/snackbar.css';
import '@justgiving/carepack-drawer/styles.css';
import '@justgiving/carepack-avatar/avatar.css';
import '@justgiving/carepack-popover/popover.css';
import '@justgiving/carepack-dropdown/dropdown.css';
import '@justgiving/carepack-progress/progress.css';
import '@justgiving/carepack-stepper/stepper.css';

import React, { Suspense, PropsWithChildren, useMemo } from 'react';

import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useRouteError,
} from 'react-router-dom';

import Loading from '@justgiving/loading';
import { PageComponentsProvider } from '@justgiving/page-components';

import { PageType } from '../../../types/graphql';
import { ErrorBoundary } from '../../components/ErrorBoundary/ErrorBoundary';
import { AuthProvider, Permissions } from '../../context/Auth';
import { PathInfoProvider } from '../../context/PathInfo';
import { usePathInfo } from '../../hooks/usePathInfo';
import { CharityAuthoriser } from '../../components/Authoriser/CharityAuthoriser';
import i18n from 'i18next';
import './App.scss';
import { OptimizelyProvider } from '../../components/OptimizelyProvider/OptimizelyProvider';
import { I18nextProvider } from 'react-i18next';
import { TrackingProvider } from '@justgiving/react-tracking';
import { Toaster } from '@justgiving/carepack-toast';
import ErrorPage from '@/components/ErrorPage/ErrorPage';
import { CustomError } from '@/components/ErrorBoundary/errors';

const analytics = {
  track: (data: Record<string, unknown>) => {
    window.dataLayer.push(data);
  },
};

const Auth = ({ children }: PropsWithChildren) => {
  const { pageType } = usePathInfo();

  switch (pageType) {
    case PageType.Charity:
      return (
        <CharityAuthoriser>
          <AuthProvider permissions={[Permissions.isValidCharityUser]}>
            {children}
          </AuthProvider>
        </CharityAuthoriser>
      );
    case PageType.Campaign:
      return (
        <CharityAuthoriser>
          <AuthProvider permissions={[Permissions.IsCampaignAdmin]}>
            {children}
          </AuthProvider>
        </CharityAuthoriser>
      );
    case PageType.OnePage:
    case PageType.Crowdfunding:
    default:
      return (
        <AuthProvider permissions={[Permissions.IsPageOwner]}>
          {children}
        </AuthProvider>
      );
  }
};

const fundraisingPrefixes = ['fundraising', 'page'];

const RouteErrorBoundary = () => {
  const error = useRouteError();
  return <ErrorPage error={error as Error} />;
};

export const createAppRouter = () =>
  createBrowserRouter([
    ...fundraisingPrefixes.map((prefix) => ({
      path: `${prefix}/:slug/admin`,
      errorElement: <RouteErrorBoundary />,
      lazy: async () => ({
        Component: (
          await import(
            /* webpackChunkName: "fundraising-root" */ '../Fundraising/FundraisingRoot/FundraisingRoot'
          )
        ).FundraisingRoot,
      }),
      children: [
        {
          index: true,
          element: <Navigate to="edit" replace />,
        },
        {
          path: 'dashboard',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "dashboard" */ '@/pages/shared/Dashboard/DashboardPage'
              )
            ).default,
          }),
        },
        {
          path: 'edit',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "edit" */ '@/pages/shared/Edit/EditPage'
              )
            ).default,
          }),
        },
        {
          path: 'donations/offline',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "offline-donation" */ '@/pages/shared/Donations/components/OfflineDonationsPage/OfflineDonationsPage'
              )
            ).default,
          }),
        },
        {
          path: 'donations',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "donations" */ '@/pages/shared/Donations/DonationsPage'
              )
            ).default,
          }),
        },
        {
          path: 'donations/all',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "all-donations" */ '@/pages/shared/AllDonations/AllDonationsPage'
              )
            ).default,
          }),
        },
        {
          path: 'donations/offline-all',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "all-offline-donations" */ '@/pages/shared/AllOfflineDonations/AllOfflineDonations'
              )
            ).default,
          }),
        },
        {
          path: 'updates',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "updates" */ '@/pages/shared/Updates/UpdatesPage'
              )
            ).default,
          }),
        },
        {
          path: 'share',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "share" */ '@/pages/shared/Share/Share'
              )
            ).default,
          }),
        },
        {
          path: 'settings/*',
          lazy: async () => {
            return {
              Component: (
                await import(
                  /* webpackChunkName: "settings" */ '@/pages/shared/Settings/SettingsPage'
                )
              ).default,
            };
          },
        },
      ],
    })),
    {
      path: 'crowdfunding/:slug/admin',
      errorElement: <RouteErrorBoundary />,
      lazy: async () => ({
        Component: (
          await import(
            /* webpackChunkName: "crowdfunding-root" */ '../Crowdfunding/CrowdfundingRoot'
          )
        ).default,
      }),
      children: [
        {
          index: true,
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "crowdfunding-overview" */ '@/pages/Crowdfunding/Overview/Overview'
              )
            ).default,
          }),
        },
        {
          path: 'theme/:themeId',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "crowdfunding-overview" */ '@/pages/Crowdfunding/Overview/Overview'
              )
            ).default,
          }),
        },
        {
          path: 'livestream',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "crowdfunding-livestream" */ '@/pages/Crowdfunding/Livestream'
              )
            ).default,
          }),
        },
        {
          path: 'donations',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "donations" */ '@/pages/shared/Donations/DonationsPage'
              )
            ).default,
          }),
        },
        {
          path: 'donations/all',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "all-donations" */ '@/pages/shared/AllDonations/AllDonationsPage'
              )
            ).default,
          }),
        },
        {
          path: 'edit',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "edit" */ '@/pages/shared/Edit/EditPage'
              )
            ).default,
          }),
        },
        {
          path: 'updates',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "updates" */ '@/pages/shared/Updates/UpdatesPage'
              )
            ).default,
          }),
        },
        {
          path: 'share',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "share" */ '@/pages/shared/Share/Share'
              )
            ).default,
          }),
        },
        {
          path: 'settings',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "settings" */ '@/pages/shared/Settings/SettingsPage'
              )
            ).default,
          }),
        },
      ],
    },
    {
      path: 'campaign/admin',
      errorElement: <RouteErrorBoundary />,
      lazy: async () => {
        return {
          Component: (
            await import(
              /* webpackChunkName: "campaign-root" */ '../Campaign/CampaignRoot/CampaignRoot'
            )
          ).CampaignRoot,
        };
      },
    },
    {
      path: 'charity/:slug/admin',
      errorElement: <RouteErrorBoundary />,
      lazy: async () => {
        return {
          Component: (
            await import(
              /* webpackChunkName: "charity-root" */ '../Charity/CharityRoot/CharityRoot'
            )
          ).CharityRoot,
        };
      },
      children: [
        {
          index: true,
          element: <Navigate to="edit" replace />,
        },
        {
          path: 'edit',
          lazy: async () => ({
            Component: (
              await import(
                /* webpackChunkName: "charity-edit" */ '@/pages/Charity/EditPage/EditPage'
              )
            ).default,
          }),
        },
      ],
    },
    {
      path: '*',
      element: (
        <ErrorPage
          error={new CustomError({ message: 'Page not found', code: 404 })}
        />
      ),
    },
  ]);

function App() {
  const router = useMemo(() => createAppRouter(), []);

  return (
    <ErrorBoundary>
      <PathInfoProvider>
        <Auth>
          <TrackingProvider analytics={analytics}>
            <OptimizelyProvider>
              <Suspense fallback={<Loading />}>
                <I18nextProvider i18n={i18n}>
                  <PageComponentsProvider>
                    <RouterProvider router={router} />
                  </PageComponentsProvider>
                </I18nextProvider>
              </Suspense>
            </OptimizelyProvider>
          </TrackingProvider>
        </Auth>
      </PathInfoProvider>
      <Toaster />
    </ErrorBoundary>
  );
}

export default App;
